/**
 * What the viewer draws, derived from whatever the API has reached.
 *
 * The contract (docs/developer/contracts.md §5) gives the page an `edition`
 * block, a `units` list and a `unit_id` on every block. Where a field has not
 * landed yet the derivation below reconstructs the same shape from the blocks,
 * so the screen never depends on a field arriving and never invents one that
 * has not.
 *
 * Two spaces are carried everywhere: the raster pixels the interface
 * addresses, and the points that were actually printed. Nothing here assumes a
 * page size, a column count or a pitch: all three are per-edition data.
 */

import { TYPES, typeOf, type TypeKey } from '../Page'
import type { BlockRow, PageDetail, PageHit, Region, UnitType, WordBox } from '../../lib/api'

/** Below this the recognition is doubted, in the figure and in the mark. */
export const LOW_CONFIDENCE = 0.85

/** A tab smaller than this fraction of the page is suppressed, not shrunk. */
export const MIN_TAB_HEIGHT = 0.03

export function typeKeyOf(blockType: string | null | undefined): TypeKey {
  const found = typeOf(blockType)
  return (Object.keys(TYPES) as TypeKey[]).find((key) => TYPES[key] === found) ?? 'article'
}

/** The six types in the order the toolbar prints them. */
export const TYPE_ORDER: TypeKey[] = [
  'article',
  'photograph',
  'advertisement',
  'notice',
  'listing',
  'furniture',
]

// --- page geometry ----------------------------------------------------------

export interface PageGeometry {
  widthPx: number
  heightPx: number
  ppi: number
  /** Points across and down the printed sheet, derived from the raster. */
  widthPt: number
  heightPt: number
  columnCount: number
  columnPitchPt: number | null
  route: string | null
  engine: string | null
  coordSpace: string
  label: string
  pubDate: string | null
  publication: string | null
  slug: string | null
  pageCount: number | null
}

export function pageLabel(
  sectionLabel: string | null | undefined,
  pageNumber: number | null | undefined,
): string {
  if (sectionLabel) return sectionLabel
  return pageNumber ? String(pageNumber) : '-'
}

/** The modal engine of a page's blocks: what read most of this page. */
function modalEngine(blocks: BlockRow[]): string | null {
  const counts = new Map<string, number>()
  for (const block of blocks) {
    if (!block.source_engine) continue
    counts.set(block.source_engine, (counts.get(block.source_engine) ?? 0) + 1)
  }
  let best: string | null = null
  let seen = 0
  for (const [engine, count] of counts) {
    if (count > seen) {
      seen = count
      best = engine
    }
  }
  return best
}

export function geometryOf(page: PageDetail): PageGeometry {
  const widthPx = page.width_px ?? 0
  const heightPx = page.height_px ?? 0
  const ppi = page.ppi || 300
  const edition = page.edition
  return {
    widthPx,
    heightPx,
    ppi,
    widthPt: (widthPx * 72) / ppi,
    heightPt: (heightPx * 72) / ppi,
    columnCount: edition?.column_count ?? page.column_count ?? 0,
    columnPitchPt: edition?.column_pitch_pt ?? null,
    route: edition?.route ?? (page.source_type === 'digital' ? 'A' : 'B'),
    engine: modalEngine(page.blocks),
    coordSpace: page.coord_space,
    label: pageLabel(page.section_label, page.page_number),
    pubDate: page.pub_date,
    publication: edition?.publication_name ?? null,
    slug: edition?.publication_slug ?? null,
    pageCount: edition?.page_count ?? null,
  }
}

export const toPt = (px: number, ppi: number) => (px * 72) / ppi

/** The column an x coordinate falls in, one-based. Floors, never rounds. */
export function columnAt(x: number, geometry: PageGeometry): number | null {
  if (!geometry.columnCount || !geometry.widthPx) return null
  const index = Math.floor((x / geometry.widthPx) * geometry.columnCount)
  return Math.min(geometry.columnCount, Math.max(1, index + 1))
}

export function columnsLabel(bbox: Region, geometry: PageGeometry): string {
  const first = columnAt(bbox[0], geometry)
  const last = columnAt(Math.max(bbox[0], bbox[2] - 1), geometry)
  if (!first || !last || !geometry.columnCount) return '-'
  const span = first === last ? `${first}` : `${first}–${last}`
  return `${span} of ${geometry.columnCount}`
}

/** How many columns wide a region measures, in the edition's own pitch. */
export function measureLabel(bbox: Region, geometry: PageGeometry): string {
  if (!geometry.columnPitchPt) return '-'
  const widthPt = toPt(bbox[2] - bbox[0], geometry.ppi)
  return `${(widthPt / geometry.columnPitchPt).toFixed(2)} col`
}

export function geometryLine(geometry: PageGeometry): string {
  const size = `${Math.round(geometry.widthPt)} × ${Math.round(geometry.heightPt)} pt`
  if (!geometry.columnCount) return size
  const pitch = geometry.columnPitchPt ? ` @ ${Math.round(geometry.columnPitchPt)} pt` : ''
  return `${size} · ${geometry.columnCount} col${pitch}`
}

export function rasterLine(geometry: PageGeometry): string {
  return `${geometry.coordSpace} ${geometry.widthPx} × ${geometry.heightPx} · ${geometry.ppi} ppi`
}

// --- regions ----------------------------------------------------------------

/**
 * One outline on the page. A unit where stage 09 has written one, and
 * otherwise a group of blocks the derivation below reconstructs, so the
 * overlay carries the same regions either way.
 */
export interface RegionItem {
  key: string
  unitId: string | null
  objectId: string | null
  articleId: string | null
  /** The raw type, as the API named it. `typeOf` maps it to one of the six. */
  rawType: string
  typeKey: TypeKey
  bbox: Region
  /** The block reading order this region starts at. */
  order: number
  /** Its place in the page's own reading order, 1-based. R06 is the sixth. */
  ordinal: number
  title: string | null
  confidence: number | null
  blocks: BlockRow[]
  structural: boolean
}

function union(boxes: Region[]): Region {
  return [
    Math.min(...boxes.map((b) => b[0])),
    Math.min(...boxes.map((b) => b[1])),
    Math.max(...boxes.map((b) => b[2])),
    Math.max(...boxes.map((b) => b[3])),
  ]
}

function firstTitle(blocks: BlockRow[]): string | null {
  const headline = blocks.find((b) => b.block_type === 'headline' && b.raw_text)
  if (headline?.raw_text) return headline.raw_text.trim()
  const any = blocks.find((b) => b.raw_text && b.raw_text.trim())
  return any?.raw_text?.trim().slice(0, 120) ?? null
}

function meanConfidence(blocks: BlockRow[]): number | null {
  const values = blocks
    .map((b) => b.confidence)
    .filter((v): v is number => v !== null && v !== undefined)
  if (!values.length) return null
  return values.reduce((sum, v) => sum + v, 0) / values.length
}

/**
 * The overlay's regions.
 *
 * With `units` present each unit is a region, and every block outside one that
 * the pipeline called structural is drawn as well, because a masthead and a
 * folio are on the page whether or not anybody searches for them. Without
 * `units` the same grouping is reconstructed from `article_id`, which is what
 * stage 06 writes and what the unit rows are built over.
 */
export function buildRegions(page: PageDetail): RegionItem[] {
  const byId = new Map(page.blocks.map((block) => [block.id, block]))
  const regions: RegionItem[] = []
  const claimed = new Set<string>()

  for (const unit of page.units ?? []) {
    const blocks = (unit.block_ids ?? [])
      .map((id) => byId.get(id))
      .filter((b): b is BlockRow => !!b)
    blocks.forEach((block) => claimed.add(block.id))
    const bbox = unit.region ?? (blocks.length ? union(blocks.map((b) => b.bbox)) : null)
    if (!bbox) continue
    regions.push({
      key: `unit:${unit.id}`,
      unitId: unit.id,
      objectId: unit.object_id,
      articleId: unit.unit_type === 'article' ? unit.object_id : null,
      rawType: unit.unit_type,
      typeKey: typeKeyOf(unit.unit_type),
      bbox,
      order: blocks.length ? Math.min(...blocks.map((b) => b.reading_order)) : 0,
      ordinal: 0,
      title: unit.title ?? firstTitle(blocks),
      confidence: unit.confidence ?? meanConfidence(blocks),
      blocks,
      structural: false,
    })
  }

  if (!regions.length) {
    // No units yet. Stage 06 groups blocks into articles, and the unit rows
    // are written over that same grouping, so it is the one to reconstruct.
    const byArticle = new Map<string, BlockRow[]>()
    for (const block of page.blocks) {
      const articleId = block.article_id
      if (!articleId) continue
      const list = byArticle.get(articleId) ?? []
      list.push(block)
      byArticle.set(articleId, list)
      claimed.add(block.id)
    }
    for (const [articleId, blocks] of byArticle) {
      regions.push({
        key: `article:${articleId}`,
        unitId: null,
        objectId: articleId,
        articleId,
        rawType: 'article',
        typeKey: 'article',
        bbox: union(blocks.map((b) => b.bbox)),
        order: Math.min(...blocks.map((b) => b.reading_order)),
        ordinal: 0,
        title: firstTitle(blocks),
        confidence: meanConfidence(blocks),
        blocks,
        structural: false,
      })
    }
  }

  /* A block can name its unit even where the unit did not list the block, so
     that link is followed before anything is inferred from geometry. */
  const byUnitId = new Map(regions.filter((r) => r.unitId).map((r) => [r.unitId as string, r]))
  for (const block of page.blocks) {
    if (claimed.has(block.id) || !block.unit_id) continue
    const region = byUnitId.get(block.unit_id)
    if (!region) continue
    region.blocks.push(block)
    claimed.add(block.id)
  }

  /* A unit that arrived without its block ids still owns the blocks inside
     its own region, and those blocks are what carry the words, the per-token
     confidences and the reading order. Claiming them geometrically keeps the
     panel's text and its doubt marks correct while `block_ids` is empty. */
  const unclaimed = page.blocks.filter((block) => !claimed.has(block.id))
  const leftover: BlockRow[] = []
  for (const block of unclaimed) {
    const cx = (block.bbox[0] + block.bbox[2]) / 2
    const cy = (block.bbox[1] + block.bbox[3]) / 2
    let best: RegionItem | null = null
    for (const region of regions) {
      if (cx < region.bbox[0] || cx > region.bbox[2]) continue
      if (cy < region.bbox[1] || cy > region.bbox[3]) continue
      if (!best || area(region.bbox) < area(best.bbox)) best = region
    }
    if (best) best.blocks.push(block)
    else leftover.push(block)
  }

  for (const region of regions) {
    if (!region.blocks.length) continue
    region.order = Math.min(...region.blocks.map((block) => block.reading_order))
    if (region.confidence === null) region.confidence = meanConfidence(region.blocks)
  }

  /* What is left is structure the pipeline did not gather into a unit: a
     masthead, a folio, an index column. Grouping them per (type, column) into
     vertically contiguous runs is the same rule article regions follow, and it
     keeps a 22-line index from drawing 22 outlines over one column. */
  for (const run of groupRuns(leftover, page.height_px ?? 0)) {
    const typeKey = typeKeyOf(run[0].block_type)
    regions.push({
      key: `blocks:${run[0].id}`,
      unitId: null,
      objectId: run[0].id,
      articleId: run[0].article_id ?? null,
      rawType: run[0].block_type,
      typeKey,
      bbox: union(run.map((block) => block.bbox)),
      order: Math.min(...run.map((block) => block.reading_order)),
      ordinal: 0,
      title: firstTitle(run),
      confidence: meanConfidence(run),
      blocks: run,
      structural: TYPES[typeKey].muted,
    })
  }

  regions.sort((a, b) => a.order - b.order)
  regions.forEach((region, index) => {
    region.ordinal = index + 1
  })
  return regions
}

function area(bbox: Region): number {
  return Math.max(1, (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]))
}

/**
 * Blocks of one type in one column, split wherever the vertical gap between
 * them is wider than a line or two. The threshold is a fraction of the page
 * rather than a constant, because page height is per-edition data.
 */
function groupRuns(blocks: BlockRow[], heightPx: number): BlockRow[][] {
  const gap = (heightPx || 1) * 0.015
  const buckets = new Map<string, BlockRow[]>()
  for (const block of blocks) {
    const key = `${typeKeyOf(block.block_type)}:${block.column_index ?? '-'}`
    const list = buckets.get(key) ?? []
    list.push(block)
    buckets.set(key, list)
  }
  const runs: BlockRow[][] = []
  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => a.bbox[1] - b.bbox[1])
    let run: BlockRow[] = []
    let bottom = -Infinity
    for (const block of bucket) {
      if (run.length && block.bbox[1] - bottom > gap) {
        runs.push(run)
        run = []
      }
      run.push(block)
      bottom = Math.max(bottom, block.bbox[3])
    }
    if (run.length) runs.push(run)
  }
  return runs
}

export function countByType(regions: RegionItem[]): Record<TypeKey, number> {
  const counts: Record<TypeKey, number> = {
    article: 0,
    photograph: 0,
    advertisement: 0,
    notice: 0,
    listing: 0,
    furniture: 0,
  }
  for (const region of regions) counts[region.typeKey] += 1
  return counts
}

export function wordCountOf(region: RegionItem): number {
  return region.blocks.reduce(
    (total, block) =>
      total + (block.raw_text ? block.raw_text.trim().split(/\s+/).filter(Boolean).length : 0),
    0,
  )
}

/** The reading text of a region, with the column wrap joined and the break kept. */
export function textOf(region: RegionItem, corrected: boolean): string {
  const ordered = [...region.blocks].sort((a, b) => a.reading_order - b.reading_order)
  const parts = ordered
    .map((block) => (corrected ? block.corrected_text || block.raw_text : block.raw_text) ?? '')
    .filter((text) => text.trim())
  return parts.join('\n\n').replace(/([^\n])\n(?!\n)/g, '$1 ')
}

/** One passage of a region: what a block printed, and how sure the engine is. */
export interface Passage {
  id: string
  text: string
  confidence: number | null
  words: WordBox[] | null
}

/**
 * The headline, deck, byline and credit are printed in the panel's own header,
 * so repeating them at the top of the reading text would have the reader read
 * them twice. They are dropped only where the region has body text to show
 * instead: a headline on its own is still the record.
 */
const HEADER_BLOCKS = new Set(['headline', 'deck', 'byline', 'credit', 'kicker'])

export function passagesOf(region: RegionItem, corrected: boolean): Passage[] {
  return readingPassages(region.blocks, corrected)
}

// --- paragraphs as the paper set them ----------------------------------------

/** Text that closes a sentence, and so may close a paragraph. */
const SENTENCE_END = /[.!?:;"”’)]$/
/** A paragraph's first line is indented by about an em; half a line height is
 *  well clear of the jitter in where a justified line starts. */
const INDENT_RATIO = 0.5
/** Word boxes that disagree with the block text by more than this share of
 *  its tokens were not made from it, and the text is used instead. */
const WORD_MISMATCH = 0.2

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0
}

function capitalsOnly(text: string | undefined): boolean {
  return !!text && /\p{Lu}{2}/u.test(text) && !/\p{Ll}/u.test(text)
}

/** One line of type at a time: the reading goes back to the left, or drops. */
function linesOf(words: WordBox[]): WordBox[][] {
  const lines: WordBox[][] = []
  for (const word of words) {
    const line = lines[lines.length - 1]
    const previous = line?.[line.length - 1]
    if (!previous || word[0] < previous[0] || word[1] > previous[3]) lines.push([word])
    else line.push(word)
  }
  return lines
}

/** Two runs of text as one, rejoining a word the typesetter broke at the line
 *  end ("Whitewa-" and "ter"). A hyphen in a word that already has one, or in
 *  a number, is a real one ("18-year-" and "old"). */
function joinRuns(text: string, next: string): string {
  if (!text) return next
  const broken = /(\S+)-$/u.exec(text)
  if (broken && /^\p{Ll}/u.test(next) && !/[-\d]/.test(broken[1]) && /\p{L}$/u.test(broken[1])) {
    return text.slice(0, -1) + next
  }
  return `${text} ${next}`
}

function usableWords(block: BlockRow): WordBox[] | null {
  const words = (block.words ?? []).filter((word) => (word[4] ?? '').trim())
  if (words.length < 2) return null
  const tokens = (block.raw_text ?? '').split(/\s+/).filter(Boolean).length
  if (!tokens || Math.abs(words.length - tokens) / tokens > WORD_MISMATCH) return null
  return words
}

/**
 * A story's text as paragraphs, the way the paper set them.
 *
 * A layout block is a column of type, not a paragraph, so one block per
 * paragraph broke a sentence wherever it turned a column ("Balls could be" /
 * "purchased and the owner") and ran a column's paragraphs together. From the
 * word boxes a paragraph starts where a line is indented and the text before
 * it closes a sentence, which also keeps a drop cap's indented lines in their
 * paragraph. Without word boxes, or for an edited text, a break is kept only
 * after a sentence closes. The words themselves are never changed beyond
 * rejoining a line-end hyphen.
 */
export function readingPassages(blocks: BlockRow[], corrected: boolean): Passage[] {
  const body = blocks.filter((block) => !HEADER_BLOCKS.has(block.block_type))
  const source = body.some((block) => (block.raw_text ?? '').trim()) ? body : blocks
  const ordered = [...source].sort(
    (a, b) => (a.sequence ?? a.reading_order) - (b.sequence ?? b.reading_order),
  )

  const passages: Passage[] = []
  // The paragraph still being read, the last of `passages` until it closes.
  const open = (): Passage | undefined => passages[passages.length - 1]
  // A paragraph ends at a closed sentence, or after a line set all in capitals
  // ("THE CHRONICLE-JOURNAL"), which is a credit or a standing head and never
  // runs into the sentence after it.
  const closes = () => {
    const last = open()
    return !last || SENTENCE_END.test(last.text.trimEnd()) || capitalsOnly(last.text)
  }
  const add = (block: BlockRow, text: string, newParagraph: boolean, words: WordBox[]) => {
    if (!text) return
    const last = open()
    if (!last || newParagraph) {
      passages.push({ id: `${block.id}:${passages.length}`, text, confidence: block.confidence, words })
      return
    }
    last.text = joinRuns(last.text, text)
    if (block.confidence !== null && block.confidence !== undefined) {
      last.confidence =
        last.confidence === null ? block.confidence : Math.min(last.confidence, block.confidence)
    }
    last.words = [...(last.words ?? []), ...words]
  }

  for (const block of ordered) {
    const edited = corrected ? block.corrected_text : null
    const words = edited ? null : usableWords(block)
    if (words) {
      const lines = linesOf(words)
      const left = Math.min(...lines.map((line) => line[0][0]))
      const indent = median(words.map((word) => word[3] - word[1])) * INDENT_RATIO
      for (const line of lines) {
        const text = line.map((word) => word[4].normalize('NFKC')).join(' ')
        const afterCapitals = capitalsOnly(open()?.text)
        const indented = line[0][0] - left > indent
        add(block, text, capitalsOnly(text) || afterCapitals || (indented && closes()), line)
      }
      continue
    }
    const text = (edited || block.raw_text || '').trim()
    text.split(/\n{2,}/).forEach((paragraph, index) => {
      const flat = paragraph.replace(/\s*\n\s*/g, ' ').trim()
      add(block, flat, index > 0 || closes() || capitalsOnly(flat), block.words ?? [])
    })
  }
  return passages
}

/** Stored paragraphs, with a break that falls mid-sentence joined up. */
export function paragraphsOf(text: string): string[] {
  const paragraphs: string[] = []
  for (const part of text.split(/\n{2,}/)) {
    const flat = part.replace(/\s*\n\s*/g, ' ').trim()
    if (!flat) continue
    const last = paragraphs[paragraphs.length - 1]
    if (
      last !== undefined &&
      !SENTENCE_END.test(last.trimEnd()) &&
      !capitalsOnly(last) &&
      !capitalsOnly(flat)
    ) {
      paragraphs[paragraphs.length - 1] = joinRuns(last, flat)
    } else {
      paragraphs.push(flat)
    }
  }
  return paragraphs
}

export function hasCorrections(region: RegionItem): boolean {
  return region.blocks.some((block) => !!block.corrected_text)
}

/** Words the recognition doubted, wherever the API sent word boxes. */
export function doubtedWords(region: RegionItem): string[] {
  const doubted: string[] = []
  for (const block of region.blocks) {
    for (const word of block.words ?? []) {
      if (word[5] < LOW_CONFIDENCE && word[4]?.trim()) doubted.push(word[4])
    }
  }
  return doubted
}

// --- search in page ---------------------------------------------------------

const STOP = new Set(['the', 'a', 'an', 'of', 'and', 'in', 'to', 'for', 'on', 'at', 'is'])

/**
 * "5,000" to "5000", the way the index and the hit matcher fold it (see
 * services/numbers.py): a comma between a digit and exactly three digits
 * with no fourth after them is a thousands separator and nothing else.
 */
export function foldNumbers(text: string): string {
  return text.replace(/(?<=\d),(\d{3})(?!\d)/g, '$1')
}

export function queryTerms(query: string): string[] {
  return foldNumbers(query)
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .map((term) => term.trim())
    .filter((term) => term.length > 1 && !STOP.has(term))
}

function stemsOf(terms: string[]): string[] {
  return terms.map((term) => (term.length > 5 ? term.slice(0, term.length - 2) : term))
}

/**
 * Word boxes for the query on this page.
 *
 * `/api/pages/{id}/hits` is the contract's answer and is preferred whenever it
 * responds. Until it does, the same boxes are computed here: from the word
 * boxes when the page was asked for with `?words=true`, and otherwise from the
 * blocks, which is coarser but is still a real location on the real page
 * rather than a count with nothing behind it.
 */
export function deriveHits(page: PageDetail, query: string): PageHit[] {
  const terms = queryTerms(query)
  if (!terms.length) return []
  const stems = stemsOf(terms)
  const hits: PageHit[] = []

  for (const block of page.blocks) {
    const words: WordBox[] | null | undefined = block.words
    if (words && words.length) {
      for (const word of words) {
        const text = foldNumbers((word[4] ?? '').toLowerCase())
        if (!text) continue
        if (stems.some((stem) => text.includes(stem))) {
          hits.push({
            block_id: block.id,
            unit_id: block.unit_id ?? null,
            unit_type: null,
            bbox: [word[0], word[1], word[2], word[3]],
            text: word[4],
          })
        }
      }
      continue
    }
    const text = foldNumbers((block.raw_text ?? '').toLowerCase())
    if (!text) continue
    if (stems.some((stem) => text.includes(stem))) {
      hits.push({
        block_id: block.id,
        unit_id: block.unit_id ?? null,
        unit_type: null,
        bbox: block.bbox,
        text: block.raw_text ?? '',
      })
    }
  }
  hits.sort((a, b) => a.bbox[1] - b.bbox[1] || a.bbox[0] - b.bbox[0])
  return hits
}

export function regionForHit(hit: PageHit, regions: RegionItem[]): RegionItem | null {
  if (hit.unit_id) {
    const byUnit = regions.find((region) => region.unitId === hit.unit_id)
    if (byUnit) return byUnit
  }
  const byBlock = regions.find((region) => region.blocks.some((block) => block.id === hit.block_id))
  if (byBlock) return byBlock
  const cx = (hit.bbox[0] + hit.bbox[2]) / 2
  const cy = (hit.bbox[1] + hit.bbox[3]) / 2
  return (
    regions.find(
      (region) =>
        cx >= region.bbox[0] &&
        cx <= region.bbox[2] &&
        cy >= region.bbox[1] &&
        cy <= region.bbox[3],
    ) ?? null
  )
}

// --- the physical address ---------------------------------------------------

export function bboxToIiif(bbox: Region): string {
  return `${Math.round(bbox[0])},${Math.round(bbox[1])},${Math.round(
    bbox[2] - bbox[0],
  )},${Math.round(bbox[3] - bbox[1])}`
}

export function parseRegionParam(value: string | null): Region | null {
  if (!value) return null
  const parts = value.split(',').map((part) => Number(part.trim()))
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null
  const [x, y, w, h] = parts
  if (w <= 0 || h <= 0) return null
  return [x, y, x + w, y + h]
}

export function addressRows(bbox: Region, geometry: PageGeometry): [string, string][] {
  const w = bbox[2] - bbox[0]
  const h = bbox[3] - bbox[1]
  const pt = (value: number) => toPt(value, geometry.ppi).toFixed(1)
  return [
    ['x', `${Math.round(bbox[0])} px`],
    ['y', `${Math.round(bbox[1])} px`],
    ['w', `${Math.round(w)} px`],
    ['h', `${Math.round(h)} px`],
    ['x pt', pt(bbox[0])],
    ['y pt', pt(bbox[1])],
    ['w pt', pt(w)],
    ['h pt', pt(h)],
    ['cols', columnsLabel(bbox, geometry)],
    ['measure', measureLabel(bbox, geometry)],
  ]
}

/** The IIIF path for a region, relative to the host, as a citation prints it. */
export function iiifPath(iiifId: string | null, bbox: Region): string | null {
  if (!iiifId) return null
  let identifier = iiifId
  try {
    identifier = new URL(iiifId).pathname.replace(/^\//, '')
  } catch {
    identifier = iiifId.replace(/^https?:\/\/[^/]+\//, '')
  }
  return `${identifier}/${bboxToIiif(bbox)}/full/0/default.jpg`
}

export function permalinkPath(geometry: PageGeometry, bbox: Region | null): string {
  const slug = geometry.slug ?? 'publication'
  const date = geometry.pubDate ?? 'undated'
  const base = `/p/${slug}/${date}/${geometry.label}`
  return bbox ? `${base}?r=${bboxToIiif(bbox)}` : base
}

/**
 * What to call a region the pipeline gave no title. The block vocabulary is
 * the honest answer: a masthead is a masthead, and "Untitled" tells a reader
 * nothing they did not already know.
 */
export function regionName(rawType: string, title: string | null): string {
  if (title && title.trim()) return title.trim()
  const words = rawType.replace(/_/g, ' ').trim()
  if (!words) return typeOf(rawType).name
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function isUnitType(value: string): value is UnitType {
  return ['article', 'photograph', 'advertisement', 'notice', 'listing'].includes(value)
}
